<template>
<b-container class="global-settings-container" v-if="globalSettings != null && loaded">
<b-form @submit="false" >
	<h1 class="page-title">Global Settings</h1>
	
	<b-tabs content-class="mt-3">
		<b-tab title="Default Contacts">
			<default-contacts
			:globalSettings="globalSettings"
			@expand-all="onExpandAll"
			@collapse-all="onCollapseAll"
			/>
		</b-tab>
		<b-tab title="Auto Messages">
			<auto-messages
			v-model="globalSettings.autoMessages"
			/>
		</b-tab>
	</b-tabs>

	<b-row class="mt-4">
		<b-col>
			<b-row><b-col>
				<b-button variant="primary" type="button" @click="onSubmit">
					Save Global Settings 
					<b-spinner small label="Saving..." class="ml-2" v-if="saving"></b-spinner>
				</b-button>
			</b-col></b-row>
		</b-col>
	</b-row>

</b-form>
</b-container>
</template>

<script>
import GlobalSettings from '../../../models/GlobalSettings';
import Vue from 'vue'
import ErrorHandler from '../../../utility/ErrorHandler';
import DefaultContacts from './DefaultContacts.vue';
import AutoMessages from './AutoMessages.vue';

export default {
	name: 'EditGlobalSettings',
	docTitle: "Global Settings - Lane Closures",

	components: {
		DefaultContacts,
		AutoMessages,
	},

	data() {
		return {
			globalSettings: {
				autoMessages: [],
				// Initialize other default properties as needed
				defaultApprover: [],
				defaultContact: [],
				defaultEmergencyContact: [],
				failedReopeningClosureContact: [],
				regionalFullClosureContact: [],
				regionalClosureContact: [],
				nonLocalFullClosureContact: [],
				paintedBicycleClosureContact: [],
				protectedBicycleClosureContact: [],
				defaultParkingContact: [],
				parkingRemovalContact: [],
				permitContact: [],
				systemAccount: [],
				defaultTrafficServicesContact: [],
				defaultTrafficSignalsContact: [],
				defaultTransitContact: [],
				defaultDCZContact: [],
				cancelledClosureContact: []
			},
			loaded: false,
			saving: false,
			saveState: null,
			submissionStatus: null,
			isButtonDisabled: false
		}
	},

	mounted() {
		this.Load();
	},

	computed: {},

	watch: {
		// Watch for changes in autoMessages to provide user feedback
		'globalSettings.autoMessages': {
			handler(newVal, oldVal) {
				if (oldVal && newVal && newVal.length !== oldVal.length) {
					// Auto messages array changed, could show unsaved changes indicator
					console.log('Auto messages updated, remember to save!');
				}
			},
			deep: true
		}
	},

	methods: {
		Load() {
			this.loaded = false;
			Vue.prototype.$http({
				method: 'get',
				url: `${this.$store.state.apiUrl}/setting?r=${Math.round(Math.random() * Math.pow(10, 6))}`
			}).then(response => {
				this.globalSettings = new GlobalSettings(response.data);
				
				// Ensure autoMessages is initialized as an array
				if (!this.globalSettings.autoMessages) {
					this.globalSettings.autoMessages = [];
				}
				
				this.loaded = true;
			}).catch(error => {
				if (!ErrorHandler.HasLoginErrorCode(error)) {
					if (error.response && error.response.data) {
						this.loaded = false;
						this.transactionStatus = 'load-error';
						this.errorDetails = error.response.data;
						
						// Show error toast
						this.$bvToast.toast('Failed to load global settings. Please refresh the page.', {
							title: 'Load Error',
							variant: 'danger',
							solid: true
						});
					}
				} else {
					ErrorHandler.RedirectToLogin();
				}
			});
		},

		// All the existing contact management methods remain the same...
		onAddSystemAccount(data) {
			this.globalSettings.systemAccount = [];
			this.globalSettings.systemAccount.push(data);
		},

		onRemoveSystemAccount(index) {
			this.globalSettings.systemAccount.splice(index, 1);
		},

		onAddDefaultContact(data) {
			this.globalSettings.defaultContact.push(data);
		},

		onRemoveDefaultContact(index) {
			this.globalSettings.defaultContact.splice(index, 1);
		},

		onAddRegionalFullClosureContact(data) {
			this.globalSettings.regionalFullClosureContact.push(data);
		},

		onAddRegionalClosureContact(data) {
			this.globalSettings.regionalClosureContact.push(data);
		},

		onAddNonLocalFullClosureContact(data) {
			this.globalSettings.nonLocalFullClosureContact.push(data);
		},

		onAddPaintedBicycleClosureContact(data) {
			this.globalSettings.paintedBicycleClosureContact.push(data);
		},

		onAddProtectedBicycleClosureContact(data) {
			this.globalSettings.protectedBicycleClosureContact.push(data);
		},

		onRemoveRegionalFullClosureContact(index) {
			this.globalSettings.regionalFullClosureContact.splice(index, 1);
		},

		onRemoveRegionalClosureContact(index) {
			this.globalSettings.regionalClosureContact.splice(index, 1);
		},

		onRemoveNonLocalFullClosureContact(index) {
			this.globalSettings.nonLocalFullClosureContact.splice(index, 1);
		},

		onRemovePaintedBicycleClosureContact(index) {
			this.globalSettings.paintedBicycleClosureContact.splice(index, 1);
		},

		onRemoveProtectedBicycleClosureContact(index) {
			this.globalSettings.protectedBicycleClosureContact.splice(index, 1);
		},

		onAddDefaultTrafficSignalsContact(data) {
			this.globalSettings.defaultTrafficSignalsContact.push(data);
		},

		onRemoveDefaultTrafficSignalsContact(index) {
			this.globalSettings.defaultTrafficSignalsContact.splice(index, 1);
		},

		onAddDefaultTrafficServicesContact(data) {
			this.globalSettings.defaultTrafficServicesContact.push(data);
		},

		onRemoveDefaultTrafficServicesContact(index) {
			this.globalSettings.defaultTrafficServicesContact.splice(index, 1);
		},

		onAddDefaultEmergencyContact(data) {
			this.globalSettings.defaultEmergencyContact.push(data);
		},

		onRemoveDefaultEmergencyContact(index) {
			this.globalSettings.defaultEmergencyContact.splice(index, 1);
		},

		onAddDefaultApprover(data) {
			this.globalSettings.defaultApprover = [];
			this.globalSettings.defaultApprover.push(data);
		},

		onRemoveDefaultApprover(index) {
			this.globalSettings.defaultApprover.splice(index, 1);
		},

		onAddDefaultParkingContact(data) {
			this.globalSettings.defaultParkingContact.push(data);
		},

		onRemoveDefaultParkingContact(index) {
			this.globalSettings.defaultParkingContact.splice(index, 1);
		},

		onAddparkingRemovalContact(data) {
			this.globalSettings.parkingRemovalContact.push(data);
		},

		onRemoveDefaultParkingRemovalContact(index) {
			this.globalSettings.parkingRemovalContact.splice(index, 1);
		},

		onAddPermitContact(data) {
			this.globalSettings.permitContact.push(data);
		},

		onRemovePermitContact(index) {
			this.globalSettings.permitContact.splice(index, 1);
		},

		onAddDefaultTransitContact(data) {
			this.globalSettings.defaultTransitContact.push(data);
		},

		onRemoveDefaultTransitContact(index) {
			this.globalSettings.defaultTransitContact.splice(index, 1);
		},

		onAddCancelledClosureContact(data) {
			this.globalSettings.cancelledClosureContact.push(data);
		},

		onRemoveCancelledClosureContact(index) {
			this.globalSettings.cancelledClosureContact.splice(index, 1);
		},

		onAddFailedReopeningClosureContact(data) {
			this.globalSettings.failedReopeningClosureContact.push(data);
		},

		onRemoveFailedReopeningClosureContact(index) {
			this.globalSettings.failedReopeningClosureContact.splice(index, 1);
		},

		onAddDefaultDCZContact(data) {
			this.globalSettings.defaultDCZContact.push(data);
		},

		onRemoveDefaultDCZContact(index) {
			this.globalSettings.defaultDCZContact.splice(index, 1);
		},

		onExpandAll() {
			// Only expand if refs exist (they won't exist when using tabs)
			if (this.$refs.defaultApprover) {
				this.$refs.defaultApprover.onExpand();
				this.$refs.defaultContact.onExpand();
				this.$refs.defaultEmergencyContact.onExpand();
				this.$refs.failedReopeningClosureContact.onExpand();
				this.$refs.regionalFullClosureContact.onExpand();
				this.$refs.regionalClosureContact.onExpand();
				this.$refs.nonLocalFullClosureContact.onExpand();
				this.$refs.paintedBicycleClosureContact.onExpand();
				this.$refs.protectedBicycleClosureContact.onExpand();
				this.$refs.defaultParkingContact.onExpand();
				this.$refs.parkingRemovalContact.onExpand();
				this.$refs.permitContact.onExpand();
				this.$refs.systemAccount.onExpand();
				this.$refs.defaultTrafficServicesContact.onExpand();
				this.$refs.defaultTrafficSignalsContact.onExpand();
				this.$refs.defaultTransitContact.onExpand();
				this.$refs.defaultDCZContact.onExpand();
			}
		},

		onCollapseAll() {
			// Only collapse if refs exist (they won't exist when using tabs)
			if (this.$refs.defaultApprover) {
				this.$refs.defaultApprover.onCollapse();
				this.$refs.defaultContact.onCollapse();
				this.$refs.defaultEmergencyContact.onCollapse();
				this.$refs.failedReopeningClosureContact.onCollapse();
				this.$refs.regionalFullClosureContact.onCollapse();
				this.$refs.regionalClosureContact.onCollapse();
				this.$refs.nonLocalFullClosureContact.onCollapse();
				this.$refs.paintedBicycleClosureContact.onCollapse();
				this.$refs.protectedBicycleClosureContact.onCollapse();
				this.$refs.defaultParkingContact.onCollapse();
				this.$refs.parkingRemovalContact.onCollapse();
				this.$refs.permitContact.onCollapse();
				this.$refs.systemAccount.onCollapse();
				this.$refs.defaultTrafficServicesContact.onCollapse();
				this.$refs.defaultTrafficSignalsContact.onCollapse();
				this.$refs.defaultTransitContact.onCollapse();
				this.$refs.defaultDCZContact.onCollapse();
			}
		},

		onSubmit() {
			this.saving = true;
			this.saveState = null;
			
			// Validate auto messages before saving
			const invalidMessages = this.globalSettings.autoMessages.filter(msg => 
				!msg.conditionKey || !msg.when || !msg.messageHtml.trim()
			);
			
			if (invalidMessages.length > 0) {
				this.$bvToast.toast('Please complete all auto message rules before saving.', {
					title: 'Validation Error',
					variant: 'warning',
					solid: true
				});
				this.saving = false;
				return;
			}

			Vue.prototype.$http({
				method: 'put',
				url: `${this.$store.state.apiUrl}/setting/?r=${Math.round(Math.random() * Math.pow(10, 6))}`,
				data: this.globalSettings.getRawValue()
			}).then(() => {
				this.saveState = 'success';
				this.submissionStatus = 'submission-success';
				
				this.$bvToast.toast('Global settings saved successfully!', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
			}).catch(error => {
				this.saveState = 'fail';
				if (error) {
					if (error.response) {
						if (!ErrorHandler.HasLoginErrorCode(error)) {
							this.showErrors(error.response.data.fields);
							
							this.$bvToast.toast('Failed to save global settings. Please check the form for errors.', {
								title: 'Save Error',
								variant: 'danger',
								solid: true
							});
						} else {
							ErrorHandler.RedirectToLogin();
						}
					}
				}
			}).finally(() => {
				this.isButtonDisabled = false;
				this.saving = false;
			});
		},

		showErrors(fields) {
			// Handle field errors if needed
			console.error('Validation errors:', fields);
		}
	}
}
</script>

<style scoped>
.global-settings-container {
	padding: 2rem;
}


.page-title {
	margin-bottom: 2rem;
	color: #495057;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
	.global-settings-container {
		padding: 1rem;
	}
	
	.page-title {
		font-size: 1.5rem;
		margin-bottom: 1rem;
	}
}
</style>

<i18n>
{
	"en-CA": {
		"autoMessages": {
			"title": "Auto Message Rules",
			"description": "Define automatic internal messages sent when lane closure requests meet specific conditions.",
			"addButton": "Add Auto Message Rule",
			"editTitle": "Edit Auto Message Rule",
			"addTitle": "Add Auto Message Rule",
			"condition": "Condition",
			"when": "When",
			"messagePreview": "Message Preview",
			"actions": "Actions",
			"noRules": "No auto message rules configured. Click 'Add Auto Message Rule' to create your first rule."
		}
	}
}
</i18n>